import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import CarDetailsForm from "@/components/CarDetailsForm";
import AudioUploader from "@/components/AudioUploader";
import ProcessingSteps from "@/components/ProcessingSteps";
import MFCWLResults from "@/components/MFCWLResults";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Upload, RotateCcw, Lock, Loader2, Copy } from "lucide-react";

const STEP_MAP = {
  queued: 1, processing: 1, loading_audio: 1, validating: 2,
  noise_cancelling: 3, analyzing_sound: 4, ai_analyzing: 5,
  completed: 6, error: 0,
};

const TURBO_LABEL = { yes: "Turbo: Yes", no: "Turbo: No", unknown: "Turbo: Not confirmed" };

const TurboBadge = ({ carDetails }) => {
  const status = carDetails?.turbo_status;
  if (!status) return null;
  return (
    <p className="text-xs text-slate-500 mt-1" data-testid="mfcwl-turbo-status" data-turbo-status={status}>
      <span className="font-semibold text-slate-700">{TURBO_LABEL[status]}</span>
      {" "}— auto-detected{carDetails.turbo_detection?.reason ? `: ${carDetails.turbo_detection.reason}` : ""}
    </p>
  );
};

export default function MFCWLPage() {
  const [searchParams] = useSearchParams();
  const [unlocked, setUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");

  const [carData, setCarData] = useState(null);
  const [carDetails, setCarDetails] = useState({
    make: "", model: "", year: "", transmission: "", fuel_type: "", km_driven: "", has_turbo: false
  });
  const [audioFile, setAudioFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const pollRef = useRef(null);

  // Always use India (INR) for MFCWL
  const country = "IN";

  useEffect(() => {
    // Check session storage for unlock state
    if (sessionStorage.getItem("mfcwl_unlocked") === "true") {
      setUnlocked(true);
    }
    // Auto-load analysis from URL param (e.g. /mfcwl?id=xxx from History page)
    const loadId = searchParams.get("id");
    if (loadId) {
      sessionStorage.setItem("mfcwl_unlocked", "true");
      setUnlocked(true);
      api.getSoundStatus(loadId)
        .then(res => {
          if (res.data) {
            setAnalysisResult(res.data);
            setCurrentStep(6);
            const cd = res.data.car_details || {};
            setCarDetails({
              make: cd.make || "", model: cd.model || "", year: cd.year || "",
              transmission: cd.transmission || "", fuel_type: cd.fuel_type || "",
              km_driven: cd.km_driven || "", has_turbo: false,
            });
          }
        })
        .catch(() => toast.error("Failed to load analysis"));
    }
  }, [searchParams]);

  useEffect(() => {
    if (unlocked) {
      api.getCarData(country)
        .then(r => setCarData(r.data))
        .catch(() => toast.error("Failed to load car data"));
    }
  }, [unlocked]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleCodeSubmit = () => {
    if (codeInput === "539953") {
      setUnlocked(true);
      sessionStorage.setItem("mfcwl_unlocked", "true");
    } else {
      setCodeError("Invalid access code");
    }
  };

  const isFormValid = carDetails.make && carDetails.model && carDetails.year &&
    carDetails.transmission && carDetails.fuel_type && carDetails.km_driven && audioFile;

  const pollForResults = useCallback((analysisId) => {
    let attempts = 0;
    const maxAttempts = 120;
    pollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        clearInterval(pollRef.current);
        setIsAnalyzing(false);
        setCurrentStep(0);
        toast.error("Analysis timed out. Please try again.");
        return;
      }
      try {
        const res = await api.getSoundStatus(analysisId);
        const data = res.data;
        const step = STEP_MAP[data.status] ?? 1;
        setCurrentStep(step);
        if (data.status === "completed") {
          clearInterval(pollRef.current);
          setAnalysisResult(data);
          setIsAnalyzing(false);
          toast.success("Analysis complete!");
        } else if (data.status === "error") {
          clearInterval(pollRef.current);
          setIsAnalyzing(false);
          setCurrentStep(0);
          toast.error(data.error_message || "Analysis failed");
        }
      } catch {}
    }, 2000);
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!isFormValid) {
      toast.error("Please fill all fields and upload an audio/video file");
      return;
    }
    setIsAnalyzing(true);
    setAnalysisResult(null);
    setCurrentStep(1);

    const formData = new FormData();
    formData.append("audio_file", audioFile);
    formData.append("country", country);
    formData.append("source", "mfcwl");
    // MFCWL: turbo is auto-detected on the server from make/model/year/fuel
    const details = { ...carDetails, has_turbo: "auto" };
    Object.entries(details).forEach(([k, v]) => formData.append(k, v));

    try {
      const response = await api.submitSound(formData);
      const { id, status, from_cache } = response.data;
      if (status === "completed" && from_cache) {
        toast.success("Instant result from cache!");
        try {
          const fullResult = await api.getSoundStatus(id);
          setAnalysisResult(fullResult.data);
          setIsAnalyzing(false);
          setCurrentStep(6);
        } catch {
          pollForResults(id);
        }
      } else if ((status === "processing" || status === "queued") && id) {
        pollForResults(id);
      }
    } catch (err) {
      setIsAnalyzing(false);
      setCurrentStep(0);
      toast.error(err.response?.data?.detail || "Failed to start analysis");
    }
  }, [carDetails, audioFile, isFormValid, pollForResults, country]);

  const handleReset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setCarDetails({ make: "", model: "", year: "", transmission: "", fuel_type: "", km_driven: "", has_turbo: false });
    setAudioFile(null);
    setIsAnalyzing(false);
    setAnalysisResult(null);
    setCurrentStep(0);
  };

  // Code gate modal
  if (!unlocked) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md border border-slate-200 shadow-sm">
          <CardContent className="p-8 text-center space-y-6">
            <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full flex items-center justify-center">
              <Lock size={28} className="text-slate-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold uppercase tracking-wider text-slate-800">MFCWL Access</h2>
              <p className="text-sm text-slate-400 mt-1">Enter authorized access code to continue</p>
            </div>
            <div className="space-y-3">
              <Input
                type="password"
                placeholder="Enter access code..."
                value={codeInput}
                onChange={(e) => { setCodeInput(e.target.value); setCodeError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleCodeSubmit()}
                className="text-center text-lg font-mono tracking-widest"
                data-testid="mfcwl-code-input"
                autoFocus
              />
              {codeError && <p className="text-sm text-red-500 font-medium" data-testid="mfcwl-code-error">{codeError}</p>}
              <Button onClick={handleCodeSubmit} className="w-full bg-slate-900 text-white" data-testid="mfcwl-code-submit">
                Unlock
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Results view
  if (analysisResult) {
    const copyId = () => {
      navigator.clipboard.writeText(analysisResult.id || "");
      toast.success("Analysis ID copied");
    };

    return (
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight uppercase font-['Barlow'] text-slate-900" data-testid="mfcwl-title">
              MFCWL Inspection Report
            </h1>
            <p className="text-sm text-slate-500">
              {analysisResult.car_details?.make} {analysisResult.car_details?.model} ({analysisResult.car_details?.year}) — {analysisResult.car_details?.km_driven?.toLocaleString()} km
            </p>
            <TurboBadge carDetails={analysisResult.car_details} />
            <button onClick={copyId} className="text-xs text-slate-400 font-mono hover:text-slate-600 mt-1 flex items-center gap-1" data-testid="mfcwl-copy-id">
              ID: {analysisResult.id} <Copy size={12} />
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={handleReset} className="gap-2" data-testid="mfcwl-new-analysis-btn">
            <RotateCcw size={14} /> New Inspection
          </Button>
        </div>
        <MFCWLResults result={analysisResult} />
      </div>
    );
  }

  // Input form
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow'] text-slate-900" data-testid="mfcwl-page-title">
          MFCWL Engine Inspection
        </h1>
        <p className="text-base text-slate-500 mt-1">Mahindra First Choice Wheels — Engine sound diagnostic</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Vehicle Details */}
        <div className="lg:col-span-2">
          <Card className="border border-slate-200 shadow-none rounded-sm">
            <CardContent className="p-5">
              <CarDetailsForm
                carData={carData}
                carDetails={carDetails}
                onChange={setCarDetails}
                hideTurbo={true}
              />
            </CardContent>
          </Card>
        </div>

        {/* Audio Upload */}
        <div>
          <Card className="border border-slate-200 shadow-none rounded-sm">
            <CardContent className="p-5">
              <AudioUploader audioFile={audioFile} setAudioFile={setAudioFile} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Analyze Button */}
      <div className="flex gap-3">
        <Button
          size="lg"
          onClick={handleAnalyze}
          disabled={!isFormValid || isAnalyzing}
          className="bg-[#003399] hover:bg-[#002277] text-white rounded-sm uppercase tracking-wider font-bold text-sm px-8 py-5"
          data-testid="mfcwl-analyze-btn"
        >
          {isAnalyzing ? <><Loader2 size={16} className="mr-2 animate-spin" /> Analyzing...</> : <><Upload size={16} className="mr-2" /> Run Inspection</>}
        </Button>
        <Button variant="outline" size="lg" onClick={handleReset} className="rounded-sm uppercase tracking-wider font-bold text-sm px-6 py-5">
          <RotateCcw size={16} className="mr-2" /> Reset
        </Button>
      </div>

      {/* Processing Steps */}
      {isAnalyzing && <ProcessingSteps currentStep={currentStep} />}
    </div>
  );
}
